import {
  Company,
  CompanyList,
  CompanyUser,
  CompanyUserList,
  CreateCompanyUserInput,
  CreateCompanyUserResponse,
  UpdateCompanyUserInput,
} from '../interfaces/companies';
import { empty, json, problem, type PathDef } from '../../util/pathDef';

export const companiesGet: PathDef = {
  method: 'get',
  path: '/companies',
  handler: 'domains/bff/src/companies/list/index.handler',
  tags: ['companies'],
  summary: 'List companies referred by the caller',
  description: 'Owner or admin: companies referred by the caller. All-companies management is Admin Center `/admin/companies`.',
  security: 'bearerAuth',
  responses: {
    200: json('Referred companies', CompanyList),
    401: problem('Unauthorized'),
    404: problem('Not found'),
    502: problem('Users service unavailable'),
  },
};

export const companyUsersGet: PathDef = {
  method: 'get',
  path: '/companies/{companyKey}/users',
  handler: 'domains/bff/src/companies/users/list/index.handler',
  tags: ['companies'],
  summary: 'List Cloud users for a referred company',
  description: 'Lists Cloud users for a company the caller owns or referred.',
  security: 'bearerAuth',
  responses: {
    200: json('Company users', CompanyUserList),
    400: problem('Invalid request'),
    401: problem('Unauthorized'),
    404: problem('Not found'),
    502: problem('Users service unavailable'),
  },
};

export const companyUsersPost: PathDef = {
  method: 'post',
  path: '/companies/{companyKey}/users',
  handler: 'domains/bff/src/companies/users/post/index.handler',
  tags: ['companies'],
  summary: 'Create a Cloud user for a referred company',
  description: 'Creates a Cloud user. They set a password from the login page.',
  security: 'bearerAuth',
  request: CreateCompanyUserInput,
  responses: {
    200: json('User created', CreateCompanyUserResponse),
    400: problem('Invalid request'),
    401: problem('Unauthorized'),
    404: problem('Not found'),
    409: problem('Email already in use'),
    502: problem('Users service unavailable'),
  },
};

export const companyUsersDelete: PathDef = {
  method: 'delete',
  path: '/companies/{companyKey}/users/{userId}',
  handler: 'domains/bff/src/companies/users/delete/index.handler',
  tags: ['companies'],
  summary: 'Delete a Cloud user',
  description: 'Soft-deletes a Cloud user that belongs to a company the caller owns or referred.',
  security: 'bearerAuth',
  responses: {
    204: empty('User deleted'),
    400: problem('Invalid request'),
    401: problem('Unauthorized'),
    404: problem('Not found'),
    502: problem('Users service unavailable'),
  },
};

export const companyGet: PathDef = {
  method: 'get',
  path: '/companies/{companyKey}',
  handler: 'domains/bff/src/companies/get/index.handler',
  tags: ['companies'],
  summary: 'Get a company the caller can manage',
  description: 'Owner or admin of that company or its referrer.',
  security: 'bearerAuth',
  responses: {
    200: json('Company', Company),
    400: problem('Invalid request'),
    401: problem('Unauthorized'),
    404: problem('Not found'),
    502: problem('Users service unavailable'),
  },
};

export const companyUsersPut: PathDef = {
  method: 'put',
  path: '/companies/{companyKey}/users/{userId}',
  handler: 'domains/bff/src/companies/users/put/index.handler',
  tags: ['companies'],
  summary: 'Update a company user',
  description: 'Sets role at or below the caller’s role, or reinstates a soft-deleted user (`active: true`).',
  security: 'bearerAuth',
  request: UpdateCompanyUserInput,
  responses: {
    200: json('Updated user', CompanyUser),
    400: problem('Invalid request'),
    401: problem('Unauthorized'),
    403: problem('Forbidden'),
    404: problem('Not found'),
    502: problem('Users service unavailable'),
  },
};

export const companiesPaths: PathDef[] = [companiesGet, companyGet, companyUsersGet, companyUsersPost, companyUsersPut, companyUsersDelete];

const adminCompany = (path: string) => `/admin${path}`;

export const adminCompaniesGet: PathDef = {
  ...companiesGet,
  path: adminCompany(companiesGet.path),
  handler: 'domains/bff/src/admin/companies/list/index.handler',
  tags: ['admin'],
  summary: 'List every cloud company',
  description: 'Admin Center. Any Admin Center session can list every company.',
};

export const adminCompanyGet: PathDef = {
  ...companyGet,
  path: adminCompany(companyGet.path),
  handler: 'domains/bff/src/admin/companies/get/index.handler',
  tags: ['admin'],
  summary: 'Get any cloud company',
  description: 'Admin Center. Any Admin Center session can read any company.',
};

export const adminCompanyUsersGet: PathDef = {
  ...companyUsersGet,
  path: adminCompany(companyUsersGet.path),
  handler: 'domains/bff/src/admin/companies/users/list/index.handler',
  tags: ['admin'],
  summary: 'List users of any cloud company',
  description: 'Admin Center. Any Admin Center session can list users of any company.',
};

export const adminCompanyUsersPost: PathDef = {
  ...companyUsersPost,
  path: adminCompany(companyUsersPost.path),
  handler: 'domains/bff/src/admin/companies/users/post/index.handler',
  tags: ['admin'],
  summary: 'Create a user on any cloud company',
  description: 'Admin Center. Any Admin Center session can create a user. They set a password from the web login page.',
};

export const adminCompanyUsersPut: PathDef = {
  ...companyUsersPut,
  path: adminCompany(companyUsersPut.path),
  handler: 'domains/bff/src/admin/companies/users/put/index.handler',
  tags: ['admin'],
  summary: 'Update a user on any cloud company',
  description: 'Admin Center. Any Admin Center session acts as owner.',
};

export const adminCompanyUsersDelete: PathDef = {
  ...companyUsersDelete,
  path: adminCompany(companyUsersDelete.path),
  handler: 'domains/bff/src/admin/companies/users/delete/index.handler',
  tags: ['admin'],
  summary: 'Deactivate a user on any cloud company',
  description: 'Admin Center. Any Admin Center session can deactivate a user.',
};

export const adminCompaniesPaths: PathDef[] = [
  adminCompaniesGet,
  adminCompanyGet,
  adminCompanyUsersGet,
  adminCompanyUsersPost,
  adminCompanyUsersPut,
  adminCompanyUsersDelete,
];
